import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const NotifCtx = createContext(null);
const POLL_INTERVAL_MS = 15000;

export function NotificationsProvider({ children }) {
  const { user } = useAuth();
  const [unread, setUnread] = useState(0);
  const rollbackRef = useRef(0);
  const inFlightRef = useRef(false);

  const refresh = useCallback(async () => {
    if (!user) { setUnread(0); return; }
    // Guard: avoid overlapping requests.
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    try {
      const r = await api.get("/notifications/unread-count");
      const value = Math.max(0, Number(r.data?.unread || 0));
      setUnread(value);
    } catch (e) { /* ignore */ }
    finally { inFlightRef.current = false; }
  }, [user]);

  useEffect(() => {
    // Immediate refresh on mount and on user change.
    refresh();
    if (!user) return; // do not poll when logged out
    const id = setInterval(refresh, POLL_INTERVAL_MS);
    const onFocus = () => refresh();
    const onVisibility = () => {
      if (document.visibilityState === "visible") refresh();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      clearInterval(id);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [refresh, user]);

  const optimisticDecrement = useCallback((by = 1) => {
    rollbackRef.current = unread;
    setUnread((u) => Math.max(0, u - by));
  }, [unread]);

  const optimisticZero = useCallback(() => {
    rollbackRef.current = unread;
    setUnread(0);
  }, [unread]);

  const rollback = useCallback(() => {
    setUnread(rollbackRef.current);
  }, []);

  return (
    <NotifCtx.Provider value={{ unread, refresh, optimisticDecrement, optimisticZero, rollback, setUnread }}>
      {children}
    </NotifCtx.Provider>
  );
}

export function useNotifications() {
  const ctx = useContext(NotifCtx);
  if (!ctx) throw new Error("useNotifications must be used within NotificationsProvider");
  return ctx;
}
